/**
 * Shared validation logic for the dynamic form engine (v2).
 *
 * Used by:
 *   - frontend: ValidationEngine.ts (re-exports from here)
 *   - backend:  lib/formValidation.ts (imported via @livingsyncs/types)
 *
 * Operates on FormSchemaV2 directly so section-level visibleIf is respected —
 * fields inside a hidden section are excluded from validation entirely.
 */

import type { FormSchemaV2, FieldElement, ValidationRules } from "./forms";
import { isFieldVisible, isFieldRequired } from "./formConditions";
import {
  BODY_REGION_VALUES,
  SKIN_FINDING_TYPE_VALUES,
  INCIDENT_SEVERITY_VALUES,
} from "./enums";

type FormValues = Record<string, unknown>;

const BODY_REGION_SET = new Set<string>(BODY_REGION_VALUES);
const SKIN_FINDING_TYPE_SET = new Set<string>(SKIN_FINDING_TYPE_VALUES);
const INCIDENT_SEVERITY_SET = new Set<string>(INCIDENT_SEVERITY_VALUES);

/**
 * US-188: type-aware value check for the body_map field type — the shared
 * generic validateValue()/validateField() below have no per-field-type
 * dispatch, so this is a separate, explicitly-called function rather than
 * folded into that generic walk (keeps every other field type's validation
 * path untouched — see the US-188 spec's cycle-1 review for why a
 * cross-cutting change to validateField/validateValue was rejected).
 */
export function validateBodyMapValue(
  element: FieldElement,
  value: unknown,
): string[] {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value)) {
    return [`${element.label} must be a list of findings`];
  }
  const errors: string[] = [];
  value.forEach((entry, i) => {
    if (typeof entry !== "object" || entry === null) {
      errors.push(`${element.label}: entry ${i + 1} is not a valid finding`);
      return;
    }
    const e = entry as Record<string, unknown>;
    if (typeof e.region !== "string" || !BODY_REGION_SET.has(e.region)) {
      errors.push(`${element.label}: entry ${i + 1} has an invalid region`);
    }
    if (
      typeof e.findingType !== "string" ||
      !SKIN_FINDING_TYPE_SET.has(e.findingType)
    ) {
      errors.push(
        `${element.label}: entry ${i + 1} has an invalid finding type`,
      );
    }
    if (
      typeof e.severity !== "string" ||
      !INCIDENT_SEVERITY_SET.has(e.severity)
    ) {
      errors.push(`${element.label}: entry ${i + 1} has an invalid severity`);
    }
  });
  return errors;
}

/**
 * Validate a single raw value against a rule set. The label is only used to
 * build the human-readable messages. Shared by the dynamic form engine (via
 * validateField) and hand-built app forms (via rulesToMantineValidate on the
 * frontend and the Zod schema bounds on the backend) — US-080.
 */
export function validateValue(
  label: string,
  rules: ValidationRules,
  value: unknown,
): string[] {
  const errors: string[] = [];

  const isEmpty =
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0);

  if (rules.required && isEmpty) {
    errors.push(`${label} is required`);
    return errors;
  }

  if (isEmpty) return errors;

  const str = typeof value === "string" ? value : String(value);
  const num = typeof value === "number" ? value : parseFloat(str);

  if (rules.minLength !== undefined && str.length < rules.minLength) {
    errors.push(`${label} must be at least ${rules.minLength} characters`);
  }

  if (rules.maxLength !== undefined && str.length > rules.maxLength) {
    errors.push(`${label} must be at most ${rules.maxLength} characters`);
  }

  if (rules.min !== undefined && !isNaN(num) && num < rules.min) {
    errors.push(`${label} must be at least ${rules.min}`);
  }

  if (rules.max !== undefined && !isNaN(num) && num > rules.max) {
    errors.push(`${label} must be at most ${rules.max}`);
  }

  if (rules.regex) {
    try {
      if (!new RegExp(rules.regex).test(str)) {
        errors.push(rules.regexMessage ?? `${label} has an invalid format`);
      }
    } catch {
      // malformed regex in template — skip silently
    }
  }

  return errors;
}

/** Validate a single field value against its rules. Returns array of error messages (empty = valid). */
export function validateField(
  element: FieldElement,
  value: unknown,
  values: FormValues,
): string[] {
  const rules: ValidationRules = element.validation ?? {};
  // requiredIf is resolved here so validateValue stays a pure rules check.
  const required = isFieldRequired(rules.required, element.requiredIf, values);
  // body_map (US-188, review cycle 1 fix — C1): an empty array is ALWAYS a
  // valid, complete "checked, nothing found" result (spec §3/§5/§8), even on
  // a field marked required — unlike every other array-valued field type
  // (checkbox/multiselect), where an empty selection legitimately means
  // "nothing chosen yet" and required correctly rejects it. The generic
  // validateValue() below has no per-type dispatch and treats every empty
  // array as an unanswered required field; body_map must bypass that rule
  // rather than let the admin-configurable `required` flag make "checked,
  // nothing found" unsubmittable, which defeats the entire point of the
  // field type. validateBodyMapValue (called separately by the backend/
  // frontend, formValidation.ts) still enforces shape on non-empty entries.
  if (element.type === "body_map") {
    return validateValue(element.label, { ...rules, required: false }, value);
  }
  return validateValue(element.label, { ...rules, required }, value);
}

/**
 * Validate all visible fields in a v2 schema.
 * Respects section-level and field-level visibleIf — hidden fields are skipped.
 * Returns fieldId → error[] map (empty map = valid).
 */
export function validateForm(
  schema: FormSchemaV2,
  values: FormValues,
): Record<string, string[]> {
  const fieldErrors: Record<string, string[]> = {};

  for (const section of schema.sections) {
    if (!isFieldVisible(section.visibleIf, values)) continue;

    for (const element of section.elements) {
      if (element.kind !== "field") continue;
      if (!isFieldVisible(element.visibleIf, values)) continue;

      const errors = validateField(element, values[element.id], values);
      if (errors.length > 0) {
        fieldErrors[element.id] = errors;
      }
    }
  }

  return fieldErrors;
}

/** True when validateForm returns zero errors. */
export function isFormValid(schema: FormSchemaV2, values: FormValues): boolean {
  return Object.keys(validateForm(schema, values)).length === 0;
}
